import { createServerFn } from "@tanstack/react-start";
import { z } from "zod"; // Import zod for validation

export const ChangelogEntrySchema = z.object({
  slug: z.string(),
  title: z.string(),
  date: z.coerce.date(), // Use coerce.date to parse string to Date
  description: z.string().optional(),
  version: z.string().optional(),
  platforms: z.array(z.string()).optional(),
  contentHtml: z.string(),
});

export type ChangelogEntry = z.infer<typeof ChangelogEntrySchema>;

// Server function to fetch changelog entries
export const getChangelog = createServerFn({ method: "GET" }).handler(async () => {
  // Dynamic imports for server-side code
  const fs = await import("node:fs/promises");
  const path = await import("node:path");
  const matter = (await import("gray-matter")).default;
  const { marked } = await import("marked");

  const changelogDirectory = path.join(process.cwd(), "content/changelog");
  const fileNames = await fs.readdir(changelogDirectory);
  const allChangelogDataPromises = fileNames
    .filter((fileName) => fileName.endsWith(".md")) // Ensure we only process markdown files
    .map(async (fileName) => {
      try {
        // Read markdown file as string
        const fullPath = path.join(changelogDirectory, fileName);
        const fileContents = await fs.readFile(fullPath, "utf8");

        // Use gray-matter to parse the metadata section
        const matterResult = matter(fileContents);
        const slug = matterResult.data.slug || fileName.replace(/\.md$/, "");

        // Use marked to convert markdown content into HTML
        const contentHtml = await marked(matterResult.content);

        // Combine data and validate using Zod
        const rawData = {
          slug,
          ...matterResult.data,
          contentHtml,
        };

        // Validate and parse the data, especially the date
        const parsedData = ChangelogEntrySchema.safeParse(rawData);

        if (!parsedData.success) {
          console.error(`Error parsing frontmatter for ${fileName}:`, parsedData.error.issues);
          return null; // Return null for invalid entries
        }

        return parsedData.data; // Return the validated data
      } catch (error) {
        console.error(`Error processing file ${fileName}:`, error);
        return null; // Return null if any error occurs during processing
      }
    });

  const allChangelogDataResults = await Promise.all(allChangelogDataPromises);

  // Filter out null entries (those that failed parsing/validation)
  const validChangelogData = allChangelogDataResults.filter(
    (entry): entry is ChangelogEntry => entry !== null,
  );

  // Sort entries by date (newest first) - now guaranteed to have valid dates
  return validChangelogData.sort((a, b) => {
    // getTime() is safe now because Zod ensures 'date' is a valid Date object
    return b.date.getTime() - a.date.getTime();
  });
});
